/**
 * Telegram-style Client-Side Image Compressor
 * Resizes and compresses images in the browser using HTML5 Canvas before uploading.
 * Reduces bandwidth usage and provides instant snappy uploads for large camera photos.
 */
export async function compressImageFile(
  file: File,
  maxDimension: number = 1920,
  quality: number = 0.85
): Promise<File> {
  // Only process images (skip gifs, svgs, and non-image files)
  if (!file || !file.type || !file.type.startsWith('image/')) {
    return file;
  }

  const lowerName = file.name.toLowerCase();
  if (lowerName.endsWith('.gif') || lowerName.endsWith('.svg')) {
    return file;
  }

  // If the file is already small (under 350KB), skip compression to preserve original quality
  if (file.size < 350 * 1024) {
    return file;
  }

  return new Promise<File>((resolve) => {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const img = new Image();
      img.onload = () => {
        try {
          let { width, height } = img;

          // If image dimensions exceed maxDimension, calculate proportional scale
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = Math.round((height * maxDimension) / width);
              width = maxDimension;
            } else {
              width = Math.round((width * maxDimension) / height);
              height = maxDimension;
            }
          }

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve(file);
            return;
          }

          // Fill transparent background with white if JPEG, or keep transparent if PNG/WebP
          const targetMime = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
          if (targetMime === 'image/jpeg') {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, width, height);
          }

          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob && blob.size < file.size) {
                let outputName = file.name;
                if (targetMime === 'image/jpeg' && !lowerName.endsWith('.jpg') && !lowerName.endsWith('.jpeg')) {
                  outputName = file.name.replace(/\.[^/.]+$/, '') + '.jpg';
                }
                const compressedFile = new File([blob], outputName, {
                  type: targetMime,
                  lastModified: Date.now()
                });
                resolve(compressedFile);
              } else {
                resolve(file);
              }
            },
            targetMime,
            quality
          );
        } catch (err) {
          console.warn('[ImageCompressor] Error during canvas compression, using original:', err);
          resolve(file);
        }
      };

      img.onerror = () => resolve(file);
      img.src = e.target.result;
    };

    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
}

export async function compressMultipleFiles(
  files: File[],
  maxDimension: number = 1920,
  quality: number = 0.85,
  concurrency: number = 3
): Promise<File[]> {
  if (!files || files.length === 0) return [];
  const results: File[] = [];
  for (let i = 0; i < files.length; i += concurrency) {
    const chunk = files.slice(i, i + concurrency);
    const compressedChunk = await Promise.all(
      chunk.map((file) => compressImageFile(file, maxDimension, quality))
    );
    results.push(...compressedChunk);
  }
  return results;
}
